import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import { HTTP_403 } from '@/src/constants/common';
import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { useCampaigns } from '@/src/store/campaigns.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { getErrorMessage } from '@/src/utils/errors.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  Box,
  Button,
  Container,
  ContentLayout,
  FormField,
  Header,
  Input,
  InputProps,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import JoinEventErrorModal from '../../ui/molecules/JoinEventErrorModal/JoinEventErrorModal';
import WatchGuideLink from '../../common/WatchGuideLink';
import { participantsJoiningEventVideo } from '@/src/utils/jam-urls';
import './index.scss';

const JoinAnEvent: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const [secretKey, setSecretKey] = useState('');
  const [secretKeyError, setSecretKeyError] = useState('');
  const secretKeyInputRef = useRef<InputProps.Ref>();

  const history = useHistory();
  const { addErrorFlashbar, clearFlashbars } = useFlashbars();
  const { resetEvent } = useJamEventDetails();
  const { getCampaignEventDetails } = useCampaigns();

  const [spinner, setSpinner] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);

  const onJoinSubmit = async (key: string) => {
    try {
      setSpinner(true);
      resetEvent();

      const campaignEventDetails = await getCampaignEventDetails(key);
      const eventType = campaignEventDetails.type;

      if (eventType === 'CAMPAIGN_GROUP') {
        const eventName = campaignEventDetails?.eventName || '';
        history.push(JAM_EVENT_DETAILS_ROUTES.Challenges.resolve(eventName));
      } else {
        if (!campaignEventDetails.eventPath) {
          throw new Error('Event not found.');
        }
        history.push(JAM_EVENT_DETAILS_ROUTES.Challenges.resolve(campaignEventDetails.eventPath));
      }
    } catch (e: any) {
      if (e?.status === HTTP_403) {
        setErrorModalVisible(true);
      } else {
        addErrorFlashbar(getErrorMessage(e));
      }
    }

    setSpinner(false);
  };

  const joinEventClick = () => {
    clearFlashbars();

    if (!secretKey.trim()) {
      setSecretKeyError(t(i18nKeys.myJams.joinAnEvent.secretKeyError));
      secretKeyInputRef.current?.focus();
      return;
    }

    void onJoinSubmit(secretKey);
    setSecretKey('');
  };

  if (spinner) {
    return <JamSpinner />;
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h2"
          info={
            <WatchGuideLink
              videoUrl={participantsJoiningEventVideo}
              title={t(i18nKeys.myJams.joinAnEvent.joiningGuideLink)}
            />
          }
        >
          {t(i18nKeys.myJams.joinAnEvent.title)}
        </Header>
      }
    >
      <div className="container-custom">
        <Container header={<Header variant="h3">{t(i18nKeys.myJams.joinAnEvent.heading)}</Header>}>
          <TextContent>
            <p className="description">{t(i18nKeys.myJams.joinAnEvent.description)}</p>
          </TextContent>
          <SpaceBetween size="xs">
            <Box data-classname="join-an-event">
              <TextContent>
                <p className="text_secrets">{t(i18nKeys.myJams.joinAnEvent.secretKey)}</p>
              </TextContent>
              <div className="form_field">
                <FormField errorText={secretKeyError} i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}>
                  <Input
                    type="password"
                    onChange={({ detail }) => {
                      setSecretKeyError('');
                      setSecretKey(detail.value);
                    }}
                    value={secretKey}
                    ref={secretKeyInputRef as any}
                    ariaLabel={t(i18nKeys.myJams.joinAnEvent.secretKey)}
                    data-testid="secret-input"
                  />
                </FormField>
                <Box float="right">
                  <Button variant="primary" loading={spinner} onClick={joinEventClick} data-testid="submit-button">
                    {t(i18nKeys.myJams.joinAnEvent.btnText)}
                  </Button>
                </Box>
              </div>
            </Box>
          </SpaceBetween>
        </Container>
        <JoinEventErrorModal visible={errorModalVisible} handleClose={() => setErrorModalVisible(false)} />
      </div>
    </ContentLayout>
  );
};

export default JoinAnEvent;
